import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X, Trash2, Plus, Loader2 } from 'lucide-react';
import PillDropdown from '../../components/ui/PillDropdown';
import apiClient from '../../services/apiClient';
import { DARK, btnStyle, CARD_INGEST_SPIN_KEYFRAMES } from './theme';

/**
 * AutomationsPanel — configure "when a card with volume label X shows up,
 * pre-fill the ingest form with these settings" profiles. Set up once per
 * project; every subsequent card matching that label reuses the same
 * project/script/destination-template/remote-connection until the profile
 * is deleted — the point is NOT re-configuring this by hand for Day 2, Day
 * 3, ... of the same shoot.
 */
export default function AutomationsPanel({ c, profiles, templates, remoteConnections, destinationPresets, onRefresh, onClose, embedded = false }) {
  const isDarkMode = c === DARK;
  const [showAdd, setShowAdd] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [scriptFiles, setScriptFiles] = useState([]);
  const [form, setForm] = useState({
    name: '', matchVolumeLabel: '',
    projectMode: 'existing', projectId: '', newProjectName: '',
    scriptFileId: '', destinationId: '',
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const selectedDestination = useMemo(
    () => destinationPresets.find(d => d.id === form.destinationId) || null,
    [destinationPresets, form.destinationId]
  );

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get('/api/projects?limit=100');
        setProjects(res?.projects || res?.data?.projects || []);
      } catch (err) {
        console.error('[Automations] Failed to load projects:', err);
      } finally {
        setLoadingProjects(false);
      }
    })();
  }, []);

  // Load the selected existing project's scripts for the cross-reference picker.
  useEffect(() => {
    if (form.projectMode !== 'existing' || !form.projectId) { setScriptFiles([]); return; }
    (async () => {
      try {
        const res = await apiClient.get(`/api/projects/${form.projectId}/files`);
        setScriptFiles(Array.isArray(res) ? res : (res?.files || []));
      } catch (err) {
        console.error('[Automations] Failed to load project files:', err);
        setScriptFiles([]);
      }
    })();
  }, [form.projectMode, form.projectId]);

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const submit = useCallback(async () => {
    if (!form.name.trim() || !form.matchVolumeLabel.trim() || saving) return;
    if (form.projectMode === 'existing' && !form.projectId) return;
    if (form.projectMode === 'new' && !form.newProjectName.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      let projectId = form.projectId;
      let projectName;
      if (form.projectMode === 'new') {
        const created = await apiClient.post('/api/projects', { name: form.newProjectName.trim() });
        projectId = created?.id || created?.data?.id;
        projectName = form.newProjectName.trim();
      } else {
        projectName = projects.find(p => p.id === form.projectId)?.name || '';
      }
      const scriptFile = scriptFiles.find(f => f.id === form.scriptFileId);
      const result = await window.electronAPI.cardIngest.saveAutomationProfile({
        name: form.name.trim(),
        matchVolumeLabel: form.matchVolumeLabel.trim(),
        projectId, projectName,
        scriptFileId: form.scriptFileId || null,
        scriptFileName: scriptFile?.name || null,
        destinationId: form.destinationId || null,
      });
      if (!result?.success) throw new Error(result?.error || 'Failed to save automation profile');
      setForm({ name: '', matchVolumeLabel: '', projectMode: 'existing', projectId: '', newProjectName: '', scriptFileId: '', destinationId: '' });
      setShowAdd(false);
      await onRefresh();
    } catch (err) {
      setSaveError(err?.response?.data?.message || err.message || 'Failed to save automation profile');
    } finally {
      setSaving(false);
    }
  }, [form, saving, projects, scriptFiles, onRefresh]);

  const remove = useCallback(async (id) => {
    await window.electronAPI.cardIngest.deleteAutomationProfile(id);
    await onRefresh();
  }, [onRefresh]);

  const inputStyle = {
    width: '100%', padding: '6px 9px', fontSize: '12px', borderRadius: '6px',
    border: `1px solid ${c.rule}`, background: c.bg, color: c.text, fontFamily: 'inherit',
    boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: '10px', color: c.muted, marginBottom: '3px', display: 'block' };

  const content = (
    <>
      <style>{CARD_INGEST_SPIN_KEYFRAMES}</style>
      {!embedded && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
          <span style={{ fontSize: '13px', fontWeight: 700 }}>Automations</span>
          <button onClick={onClose} style={btnStyle(c, 'ghost')}><X size={12} /></button>
        </div>
      )}
        <div style={{ fontSize: '11px', color: c.muted, marginBottom: '14px', lineHeight: 1.5 }}>
          Set up once per project — every card whose volume label matches pre-fills the ingest form with this project, script, destination, and remote server(s), ready with one click. Keep adding cards to the same project over the life of the shoot without reconfiguring.
        </div>

        {profiles.length === 0 && !showAdd && (
          <div style={{ fontSize: '12px', color: c.muted, marginBottom: '12px' }}>No automations configured yet.</div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' }}>
          {profiles.map(p => (
            <div key={p.id} style={{ padding: '8px 10px', border: `1px solid ${c.rule}`, borderRadius: '7px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '12px', fontWeight: 600 }}>{p.name}</div>
                  <div style={{ fontSize: '10.5px', color: c.muted, fontFamily: 'monospace' }}>label: "{p.matchVolumeLabel}"</div>
                </div>
                <button onClick={() => remove(p.id)} style={btnStyle(c, 'ghost')} title="Delete automation"><Trash2 size={12} /></button>
              </div>
              <div style={{ fontSize: '10.5px', color: c.muted, marginTop: '4px' }}>
                → {p.projectName}{p.scriptFileName ? ` · cross-referenced with "${p.scriptFileName}"` : ''}
                {p.destinationId
                  ? ` · destination "${destinationPresets.find(d => d.id === p.destinationId)?.name || '(deleted)'}"`
                  // Older profiles saved before destinations were a separate module still
                  // carry their destination config inline -- keep showing it rather than
                  // silently dropping the summary for profiles nobody has re-saved yet.
                  : (p.remoteConnectionIds?.length > 0) ? ` · uploads to ${p.remoteConnectionIds.length} remote server${p.remoteConnectionIds.length === 1 ? '' : 's'}` : ''}
              </div>
            </div>
          ))}
        </div>

        {!showAdd && (
          <button onClick={() => setShowAdd(true)} style={btnStyle(c, 'ghost')}><Plus size={12} /> Add automation</button>
        )}

        {showAdd && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px', border: `1px solid ${c.rule}`, borderRadius: '8px' }}>
            <div>
              <span style={labelStyle}>Name</span>
              <input value={form.name} onChange={e => setField('name', e.target.value)} placeholder="e.g. Main Unit Camera A" style={inputStyle} />
            </div>
            <div>
              <span style={labelStyle}>Trigger — card volume label (exact match)</span>
              <input value={form.matchVolumeLabel} onChange={e => setField('matchVolumeLabel', e.target.value)} placeholder="e.g. CAM_A_001" style={inputStyle} />
            </div>

            <div>
              <span style={labelStyle}>Project</span>
              <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                <button onClick={() => setField('projectMode', 'existing')} style={{ ...btnStyle(c, form.projectMode === 'existing' ? 'primary' : 'ghost'), flex: 1 }}>Existing</button>
                <button onClick={() => setField('projectMode', 'new')} style={{ ...btnStyle(c, form.projectMode === 'new' ? 'primary' : 'ghost'), flex: 1 }}>Create new</button>
              </div>
              {form.projectMode === 'existing' ? (
                <PillDropdown
                  value={form.projectId}
                  onChange={(v) => setField('projectId', v)}
                  isDarkMode={isDarkMode}
                  options={[{ value: '', label: loadingProjects ? 'Loading…' : 'Select a project' }, ...projects.map(p => ({ value: p.id, label: p.name }))]}
                  triggerWidth="100%"
                  searchable
                  searchPlaceholder="Search projects…"
                />
              ) : (
                <input value={form.newProjectName} onChange={e => setField('newProjectName', e.target.value)} placeholder="New project name" style={inputStyle} />
              )}
            </div>

            {form.projectMode === 'existing' && form.projectId && (
              <div>
                <span style={labelStyle}>Cross-reference script (optional — for footage matching)</span>
                <PillDropdown
                  value={form.scriptFileId}
                  onChange={(v) => setField('scriptFileId', v)}
                  isDarkMode={isDarkMode}
                  options={[{ value: '', label: 'None' }, ...scriptFiles.map(f => ({ value: f.id, label: f.name }))]}
                  triggerWidth="100%"
                  searchable
                  searchPlaceholder="Search files…"
                />
              </div>
            )}

            <div>
              <span style={labelStyle}>Destination (optional — a saved destination preset)</span>
              <PillDropdown
                value={form.destinationId}
                onChange={(v) => setField('destinationId', v)}
                isDarkMode={isDarkMode}
                options={[{ value: '', label: 'Ask each time' }, ...destinationPresets.map(d => ({ value: d.id, label: d.name }))]}
                triggerWidth="100%"
              />
              {destinationPresets.length === 0 && (
                <div style={{ fontSize: '10.5px', color: c.muted, marginTop: '4px' }}>
                  No destinations saved yet — create one in the Destinations tab first.
                </div>
              )}
              {selectedDestination && (
                <div style={{ fontSize: '10.5px', color: c.muted, marginTop: '4px' }}>
                  → {templates.find(t => t.id === selectedDestination.templateId)?.name || 'no template'}{selectedDestination.rootPath ? ` — ${selectedDestination.rootPath}` : ''}
                  {selectedDestination.remoteConnectionIds?.length > 0 ? ` · ${selectedDestination.remoteConnectionIds.length} remote server${selectedDestination.remoteConnectionIds.length === 1 ? '' : 's'}` : ''}
                </div>
              )}
            </div>

            {saveError && <div style={{ fontSize: '11px', color: c.err }}>{saveError}</div>}
            <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
              <button onClick={submit} disabled={saving} style={{ ...btnStyle(c, 'primary'), opacity: saving ? 0.5 : 1 }}>
                {saving ? <Loader2 size={12} style={{ animation: 'cardIngestSpin 1s linear infinite' }} /> : null} Save automation
              </button>
              <button onClick={() => setShowAdd(false)} style={btnStyle(c, 'ghost')}>Cancel</button>
            </div>
          </div>
        )}
    </>
  );

  if (embedded) return <div style={{ width: '100%' }}>{content}</div>;

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: 520, maxHeight: '82vh', overflow: 'auto', background: c.bg, borderRadius: '10px', border: `1px solid ${c.rule}`, padding: '18px 20px' }}
      >
        {content}
      </div>
    </div>
  );
}
